import CommonTable, { type TableColumn } from "@/components/common/CommonTable";
import MainHeading from "@/components/common/MainHeading";
import Layout from "@/components/layout/Layout";
import { TriangleAlert } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { RoutesLink } from "@/utility/RouteLinks";

import { Button } from "@/components/ui/button";
import { useEffect, useState, useCallback } from "react";
import TablePagination from "@/components/common/TablePagination";
import type { IPageInfo, IFilter } from "@/interface/common.interface";
import CommonModal from "@/components/common/CommonModal";
import {
  formatTimeHM,
  GetFilterUrl,
  showToastNotification,
} from "@/utility/helper";
import { ThrowError } from "@/api/callApi";
import type {
  RegularizationItem,
} from "@/interface/regularization.interface";
import CommonFilter from "@/components/common/CommonFilter";
import moment from "moment";
import { RegularizationService } from "@/services/Regularization/RegularizationServices";
import TableActions from "@/components/common/TableActions";
import { useBranchSiteFilter } from "@/hooks/useBranchSiteFilter";

const STATUS_OPTIONS = [
  { label: "All Status", value: "" },
  { label: "Pending", value: "PENDING" },
  { label: "Approved", value: "APPROVED" },
  { label: "Rejected", value: "REJECTED" },
  { label: "Cancelled", value: "CANCELLED" },
];

const TeamRegularization: React.FC = () => {
  const navigate = useNavigate();

  const [regularizations, setRegularizations] = useState<RegularizationItem[]>(
    [],
  );
  const [isLoading, setIsLoading] = useState(false);

  const [paginationData, setPaginationData] = useState<IPageInfo>({
    currentPage: 1,
    pageSize: 10,
    total: 0,
    totalPages: 0,
  });

  const {
    isHQ,
    effectiveBranchId,
    effectiveSiteId,
    branchSiteSelectorsNode,
  } = useBranchSiteFilter({
    onChange: () => {
      setPaginationData((prev) => ({ ...prev, currentPage: 1 }));
    },
  });

  const [filter, setFilter] = useState<IFilter>({
    search: "",
    startDate: moment().startOf("month").format("YYYY-MM-DD"),
    endDate: moment().endOf("month").format("YYYY-MM-DD"),
    status: "",
  });

  // Delete modal
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState<string>("");

  // Filter handlers
  const handleSearchChange = useCallback((value: string) => {
    setFilter((prev) => ({ ...prev, search: value }));
  }, []);

  const handleFilterChange = useCallback((value: string, name: string) => {
    setFilter((prev) => ({
      ...prev,
      [name]: value,
    }));

    setPaginationData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  const handleDateRangeChange = useCallback((start: string, end: string) => {
    setFilter((prev) => ({
      ...prev,
      startDate: start,
      endDate: end,
    }));

    setPaginationData((prev) => ({
      ...prev,
      currentPage: 1,
    }));
  }, []);

  const handleChangePageSize = (value: number) => {
    setPaginationData((prev) => ({ ...prev, pageSize: value, currentPage: 1 }));
  };

  const toggleDeleteDialog = useCallback((id: string = "") => {
    setDeleteId(id);
    setDeleteDialogOpen((prev) => !prev);
  }, []);

  // Fetch data
  const fetchData = async (
    currentPage = paginationData.currentPage,
    currentLimit = paginationData.pageSize,
  ) => {
    // If Level 0 (HQ) and no branch/site selected yet, skip until loaded
    if (isHQ && !effectiveBranchId && !effectiveSiteId) return;

    setIsLoading(true);
    try {
      const apiFilter: IFilter = {
        ...filter,
        type: "team-data",
      };

      if (effectiveBranchId) {
        apiFilter.branchId = effectiveBranchId;
      }
      if (effectiveSiteId) {
        apiFilter.siteId = effectiveSiteId;
      }

      const paramsURL = GetFilterUrl(apiFilter);

      const PATH = `pageNumber=${currentPage}&pageSize=${currentLimit}${paramsURL ? `&${paramsURL}` : ""
        }`;

      const response = await RegularizationService.getList(PATH);
      if (response.status && response.data) {
        setRegularizations(response.data.regularizations || []);
        setPaginationData((prev) => ({
          ...prev,
          total: response.data.pageInfo?.total || 0,
          totalPages: response.data.pageInfo?.totalPages || 0,
        }));
      }
    } catch (err) {
      ThrowError(err);
      setRegularizations([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete handler
  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      const response = await RegularizationService.delete(deleteId, effectiveSiteId);
      if (response?.status) {
        showToastNotification(
          "success",
          response?.message || "Regularization deleted successfully",
        );
        toggleDeleteDialog("");
        fetchData();
      }
    } catch (error) {
      ThrowError(error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    paginationData.currentPage,
    paginationData.pageSize,
    filter.search,
    filter.startDate,
    filter.endDate,
    filter.status,
    effectiveBranchId,
    effectiveSiteId,
  ]);

  // Table columns
  const columns: TableColumn<RegularizationItem>[] = [
    {
      key: "sNo",
      header: "S.No",
      render: (_item: RegularizationItem, index: number) => (
        <span className="text-muted-foreground font-medium text-sm">
          {String(
            (paginationData.currentPage - 1) * paginationData.pageSize +
            index +
            1,
          ).padStart(2, "0")}
        </span>
      ),
    },
    {
      key: "name",
      header: "Name",
      render: (record) => record.user?.name || "-",
    },
    {
      key: "checkInDate",
      header: "Check-in Date",
      render: (record) =>
        record.checkInDate
          ? moment(record.checkInDate).format("DD-MM-YYYY")
          : "-",
    },
    {
      key: "checkOutDate",
      header: "Check-out Date",
      render: (record) =>
        record.checkOutDate
          ? moment(record.checkOutDate).format("DD-MM-YYYY")
          : "-",
    },
    {
      key: "duration",
      header: "Duration",
      render: (record) => {
        const map: Record<string, string> = {
          FULL_DAY: "Full Day",
          FIRST_HALF: "First Half",
          SECOND_HALF: "Second Half",
        };
        return (
          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
            {map[record.duration || ""] || record.duration || "—"}
          </span>
        );
      },
    },
    {
      key: "firstHalf",
      header: "First Half",
      render: (record) => {
        if (record.duration === "SECOND_HALF") return <span className="text-slate-400">—</span>;
        return (
          <span className="font-medium text-slate-600 text-xs">
            {formatTimeHM(record.firstHalfCheckInTime)} - {formatTimeHM(record.firstHalfCheckOutTime)}
          </span>
        );
      },
    },
    {
      key: "secondHalf",
      header: "Second Half",
      render: (record) => {
        if (record.duration === "FIRST_HALF") return <span className="text-slate-400">—</span>;
        return (
          <span className="font-medium text-slate-600 text-xs">
            {formatTimeHM(record.secondHalfCheckInTime)} - {formatTimeHM(record.secondHalfCheckOutTime)}
          </span>
        );
      },
    },
    {
      key: "reason",
      header: "Reason",
      render: (record) => record.reason || "-",
    },
    {
      key: "status",
      header: "Status",
           render: (record) => {
        const status = (record?.status || "pending").toLowerCase();
        let statusClasses = "text-amber-600 border-amber-600 bg-amber-50";
        if (status === "approved" || status === "active") {
          statusClasses = "text-green-600 border-green-600 bg-green-50";
        } else if (status === "rejected" || status === "cancelled") {
          statusClasses =
            "text-destructive border-destructive bg-destructive/10";
        }
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 text-xs font-medium border rounded-full capitalize ${statusClasses}`}
          >
            {status}
          </span>
        );
      },
    },
    {
      key: "actions",
      header: "Actions",
      render: (item: RegularizationItem) => {
        // const isPending =
        //   (item?.status || "pending").toLowerCase() === "pending";
        return (
          <TableActions
            onView={() => {
              if (item?.id)
                navigate(`${RoutesLink.teamsRegularizationDetail}/${item.id}`);
            }}
            viewLabel="View Details"
          // actions={[
          //   {
          //     label: "Edit",
          //     onClick: () =>
          //       navigate(`${RoutesLink.editRegularization}/${item.id}`),
          //     icon: <Pencil className="h-4 w-4" />,
          //     show: isPending,
          //   },
          //   {
          //     label: "Delete",
          //     onClick: () => {
          //       if (item?.id) toggleDeleteDialog(item.id);
          //     },
          //     icon: <Trash className="h-4 w-4" />,
          //     className: "text-red-600",
          //     show: isPending,
          //   },
          // ]}
          />
        );
      },
    },
  ];

  return (
    <Layout>
      <div className="space-y-6">
        <MainHeading title="Team Regularization Records" isBackButtonVisible={true} />

        <CommonFilter
          searchValue={filter.search || ""}
          onSearchChange={handleSearchChange}
          startDate={filter.startDate || ""}
          endDate={filter.endDate || ""}
          onDateRangeChange={handleDateRangeChange}
          value={filter}
          filterOptions={STATUS_OPTIONS}
          filterName="status"
          filterPlaceholder="Select Status"
          onChange={handleFilterChange}
          additionalFilters={branchSiteSelectorsNode}
        />

        <CommonTable
          columns={columns}
          data={regularizations}
          loading={isLoading}
        />
        <TablePagination
          paginationData={paginationData}
          setPaginationData={setPaginationData}
          handleChangePageSize={handleChangePageSize}
          isLoading={isLoading}
        />
      </div>

      {/* Delete Confirmation Modal */}
      <CommonModal
        show={deleteDialogOpen}
        onClose={() => toggleDeleteDialog("")}
        title=""
        className="max-w-md rounded-2xl p-0 overflow-hidden"
      >
        <div className="p-6">
          <div className="flex flex-col items-center text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-red-100">
              <TriangleAlert className="text-red-600 w-8 h-8" />
            </div>
            <h2 className="mt-5 text-2xl font-bold text-gray-900">
              Delete Regularization Request?
            </h2>
            <p className="mt-3 text-sm leading-6 text-gray-500">
              This action cannot be undone. Once deleted, the regularization
              request will be permanently removed.
            </p>
          </div>
          <div className="mt-8 flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => toggleDeleteDialog("")}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              className="flex-1"
              onClick={handleDelete}
            >
              Delete
            </Button>
          </div>
        </div>
      </CommonModal>
    </Layout>
  );
};

export default TeamRegularization;
